import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Redirect, Prompt } from "react-router-dom";
import { Spinner, BrandButton, CheckBox } from "../../assets";
import { updateShop, GET_SHOP_SUCCESS } from "../../actions/shop";
import AddressLookup from "../AddressLookup";
import OpeningTimes from "./OpeningTimes";
import DeliveryRules from "./DeliveryRules";
import { addressToString, payloadToFormData, vendorPath, rgbToString } from "../../helpers";
import ImageField from "../ImageField";
import StripeDashboardButton from "./StripeDashboardButton";
import StripeButton from "./StripeButton";
import ColorPicker from "../../assets/ColorPicker";
import TimeIntervalSelector from "../TimeIntervalField";
import PhoneField from "../PhoneField";
import ShopPreview from "../../assets/ShopPreview";

export default function Shop() {

	const dispatch = useDispatch();
	const {
		user,
		shop
	} = useSelector(state => ({ user: state.session.user, shop: state.shop }));

	const [changingAddress, setChangingAddress] = useState(false);
	const [changes, setChanges] = useState({});

	// Store an object of values for display convenience.
	const values = {
		...shop,
		...changes
	}

	useEffect(() => {
		if (!shop.loading)
			setChanges({});
	}, [shop]);

	if (!user.shop) return <Redirect to="/me" />;

	if (!shop.id) return <Spinner
		color={"#000000"}
		size={50}
		fullscreen={true}
	/>;

	function valueChange(name, value) {
		setChanges({
			...changes,
			[name]: value
		});
	}

	function inputChange(e) {
		valueChange(e.target.name, e.target.value);
    }
    
	return <div className="shop">

		{/* Stripe dashboard. */}
		<section>
			<h4>Getting paid</h4>
			{Boolean(!shop.stripe_account_id) && <div className="inner">
				To handle payments and make sure you get paid by your customers, we use <a target="_blank" rel="noopener noreferrer" href="https://stripe.com/gb">Stripe</a> as our secure payment provider.
				<br /><br />
				To open your shop and get paid online, click the button below and it will get you set up on Stripe.
				<br /><br />
				<StripeButton
					handlerUrl="/misc/stripe/connect"
					onSuccess={shop => {
						dispatch({
							type: GET_SHOP_SUCCESS,
							shop
						});
					}}
				/>
			</div>}
			{Boolean(shop.stripe_account_id) && <div className="inner">
				Great, your stripe account is connected. If you're looking for your Stripe dashboard, click the button below:
				<br /><br />
				<StripeDashboardButton handlerUrl="/misc/stripe/dashboard-link" />
			</div>}
		</section>

		<section>
			<h4>Shop name</h4>
			<div className="inner">
				<input type="text" name="name" value={values.name} onChange={inputChange} />
			</div>
		</section>

		<section>
			<h4>Contact number</h4>
			<div className="inner">
				This is really important. Your contact number will feature on your shop page so your customers can get in touch with you if anything goes wrong.
				<br/><br/>
				<PhoneField
					value={values.contactTel || ""} 
					onChange={value => valueChange("contactTel", value)}
				/>
			</div>
		</section>

		<section>
			<h4>Shop URL</h4>
			<div className="inner">
				<div>
					The address of your shop on orderabl. For example, your shop's address is <a target="_blank" rel="noopener noreferrer" href={vendorPath(shop.slug)}>https://be.orderabl.com{vendorPath(shop.slug)}</a>.
					<br /><br />
					Unfortunately, you can't edit this one. If you need to change it, please get in touch.
					<br /><br />
				</div>
				<input type="text" value={vendorPath(shop.slug)} readOnly />
			</div>
		</section>

		<section>
			<h4>Shop description</h4>
			<div className="inner">
				<textarea type="text" name="description" value={values.description || ""} onChange={inputChange} />
			</div>
		</section>

		<StatusSection status={values.address ? "good" : "bad"}>
			<h4>Shop address</h4>
			<div className="inner">
				{changingAddress || !values.address ?
					(
						<AddressLookup onChange={address => {
							valueChange("address", address);
							setChangingAddress(false);
						}} />
					) : (
						<>
							<div className="address-item">
								<span>{addressToString(values.address)}</span>
							</div>
							<BrandButton onClick={e => setChangingAddress(true)}>
								Change address
							</BrandButton>
						</>
					)
				}
			</div>
		</StatusSection>

		<section>
			<h4>Delivery settings</h4>
			<div className="inner">
				{[
					["acceptDelivery", "Accept deliveries"],
					["acceptCollection", "Accept collections"]
				].map(([field, label]) => (
					<div key={field} style={{
						padding: 10,
						marginBottom: 5,
						backgroundColor: "#f8f8f8"
					}} onClick={e => valueChange(field, !values[field])}>
						<CheckBox checked={values[field]} />
						<span style={{ marginLeft: 10 }}>{label}</span>
					</div>
				))}
			</div>
		</section>

		{values.acceptDelivery && <section>
			<h4>Delivery costs</h4>
			<div className="inner">
				<DeliveryRules
					values={values.deliveryRules || []}
					change={rules => valueChange("deliveryRules", rules)}
					disableAlpha={true}
				/>
			</div>
		</section>}

		{values.acceptDelivery && <StatusSection status={values.deliveryTimes && values.deliveryTimes.length ? "good" : "bad"}>
			<h4>Delivery hours</h4>
			<div className="inner">
				<div>Create your opening hours by selecting the weekday and the to/from times for that day. If you don't have any opening hours set, your shop will always appear as closed.</div>
				{(!values.deliveryTimes || !values.deliveryTimes.length) ?
					<h5 style={{ padding: "25px 0px" }} className="brand-c">You haven't set any opening hours yet.</h5>
					:
					<h5 style={{ padding: "25px 0px" }}>Add more opening hours:</h5>
				}
				<OpeningTimes
					values={values.deliveryTimes || []}
					change={times => valueChange("deliveryTimes", times)}
				/>
			</div>
		</StatusSection>}

		{values.acceptCollection && <StatusSection status={values.collectionTimes && values.collectionTimes.length ? "good" : "bad"}>
			<h4>Collection hours</h4>
			<div className="inner">
				<div>Create your opening hours by selecting the weekday and the to/from times for that day. If you don't have any opening hours set, your shop will always appear as closed.</div>
				{(!values.collectionTimes || !values.collectionTimes.length) ?
					<h5 style={{ padding: "25px 0px" }} className="brand-c">You haven't set any opening hours yet.</h5>
					:
					<h5 style={{ padding: "25px 0px" }}>Add more opening hours:</h5>
				}
				<OpeningTimes
					values={values.collectionTimes || []}
					change={times => valueChange("collectionTimes", times)}
				/>
			</div>
		</StatusSection>}

		<section>
			<div className="flexy" style={{ flexWrap: "wrap" }}>
				<section style={{ minWidth: "250px" }}>
					<h4>Minimum delivery time</h4>
					<div className="inner">
						How long, on average does it take you to prepare for delivery orders?
						<br /><br />
					</div>
					<div className="inner">
						<TimeIntervalSelector
							value={values.minDeliveryTime || 0}
							onChange={val => valueChange("minDeliveryTime", val)}
						/>
					</div>
				</section>
				<section style={{ minWidth: "250px" }}>
					<h4>Minimum collection time</h4>
					<div className="inner">
						How long, on average does it take you to prepare for collection orders?
						<br /><br />
					</div>
					<div className="inner">
						<TimeIntervalSelector
							value={values.minCollectionTime || 0}
							onChange={val => valueChange("minCollectionTime", val)}
						/>
					</div>
				</section>
			</div>
		</section>

		<section>
			<h4>Shop Branding</h4>
			<div className="inner">
				Here's a simplified preview of your shop branding. It will update live as you make changes. Remember to hit save to confirm!
				<ShopPreview {...values} />
			</div>
		</section>

		<section>
			<div className="flexy" style={{ flexWrap: "wrap" }}>
				<section style={{ minWidth: "250px" }}>
					<h4>Feature Color</h4>
					<div className="inner">
						Used to highlight your shop in many places. It's important that this colour contrasts against white, or certain parts of your shop won't be readable.
						<br/><br/>
						<ColorPicker
							color={values.featureColor1}
							onChange={color => valueChange("featureColor1", rgbToString(color.rgb))}
						/>
					</div>
				</section>
				{/* <section style={{ minWidth: "250px" }}>
					<h4>Color 2</h4>
					<div className="inner">
						<ColorPicker
							color={values.featureColor2}
							onChange={color => valueChange("featureColor2", rgbToString(color.rgb))}
						/>
					</div>
				</section> */}
			</div>
		</section>

		<section>
			<h4>Shop logo</h4>
			<div className="inner">
				<div>
					Your logo shows persistently on the branded site in the top left of the window. The image should work as a square. If you provide an image with a transparent background, we'll colour the transparent parts white.
					<br /><br />
				</div>
				<ImageField
					currentImage={values.logo ? (values.logo.url || values.logo) : null}
					onChange={image => {
						valueChange("logo", image);
					}}
				/>
			</div>
		</section>

		<section>
			<h4>Shop Banner</h4>
			<div className="inner">
				<div>
					This is used on the top bar of your page. The image will be partly covered up on your actual shop, so don't put anything important in it.
					<br /><br />
				</div>
				<ImageField
					aspectRatio={16 / 9}
					currentImage={values.bannerImage ? (values.bannerImage.url || values.bannerImage) : null}
					onChange={image => {
						valueChange("bannerImage", image);
					}}
				/>
			</div>
		</section>

		{/* Save prompt if there are changes. */}
		<SavePrompt
			open={Boolean(Object.keys(changes).length)}
			loading={shop.loading}
			onSave={e => {
				const formData = payloadToFormData(changes);
				dispatch(updateShop(formData));
			}}
		/>
	</div>
}

function SavePrompt({ open, loading, onSave }) {

	const leaveMessage = "You have unsaved changes. Are you sure you want to leave?";

	useEffect(() => {
		document.querySelector("body").className = open ?
			"fixed-save-prompt-open" : "fixed-save-prompt-closed";

		window.onbeforeunload = open ? function () {
			return leaveMessage;
		} : null;

	}, [open]);

	return <div className="fixed-save-prompt" style={{
		bottom: open ? 0 : -100
	}}>
		<section>
			<div className="inner">
				<div className="normalFont">
					Save your changes here.
				</div>
				<BrandButton
					onClick={onSave}
				>{loading ? <Spinner /> : "Save"}</BrandButton>
			</div>
			<Prompt
				when={open}
				message={leaveMessage}
			/>
		</section>
	</div>
}

function StatusSection({ status, children, className, ...props }) {
	return <section
		{...props}
		className={["status-section", `status-section-${status}`, className].join(" ")}
	>
		{children}
	</section>
}